import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import Header from '../components/Header'

// MA-11 — dashboard de frentes (mirror de aios_fronts no Supabase pessoal).
// Fonte da verdade = fronts.md (maestro); isto é o espelho cross-device.
const FRONTS_TOKEN = 'maestro-2026'
const AREA_ORDER = ['BLIPS', '2B', 'LAXTRO', 'AIOS', 'PESSOAL']
const AREA_COLOR: Record<string, string> = {
  BLIPS: '#60a5fa', '2B': '#4ade80', LAXTRO: '#c084fc', AIOS: '#f87171', PESSOAL: '#fbbf24',
}

interface Front {
  slug: string
  titulo: string | null
  area: string
  exec: string | null
  status: string | null
  prd_path: string | null
  gate_decision: string | null
  updated_at: string
}

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
})

export default function Fronts() {
  const [fronts, setFronts] = useState<Front[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(false)

  useEffect(() => {
    fetch('/api-fronts?k=' + encodeURIComponent(FRONTS_TOKEN))
      .then(r => r.json())
      .then((d: { fronts?: Front[] }) => { setFronts(d.fronts || []); setLoading(false) })
      .catch(() => { setErr(true); setLoading(false) })
  }, [])

  const byArea = AREA_ORDER
    .map(area => ({ area, items: fronts.filter(f => f.area === area) }))
    .filter(g => g.items.length > 0)

  const counts = {
    total: fronts.length,
    ativas: fronts.filter(f => /🟢|🟡/.test(f.status || '')).length,
    aCriar: fronts.filter(f => /🔨/.test(f.status || '')).length,
  }

  return (
    <>
      <Header />
      <main style={{ maxWidth: 880, margin: '0 auto', padding: '3rem 1.5rem 6rem' }}>
        <motion.div {...fade(0)} style={{ marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Fronts · Portfolio</p>
          <h1 style={{ fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '0.6rem' }}>
            Frentes
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 560 }}>
            Espelho do <code style={{ background: 'var(--bg2)', padding: '0.1rem 0.3rem', borderRadius: 3, fontSize: '0.8rem' }}>fronts.md</code> do maestro. 1 entregável = 1 frente. O arquivo é a fonte; isto é o mirror (aios_fronts).
          </p>
          {!loading && !err && (
            <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.25rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
              <span><b style={{ fontSize: '1.15rem' }}>{counts.total}</b> <span style={{ color: 'var(--muted)' }}>frentes</span></span>
              <span><b style={{ fontSize: '1.15rem', color: '#4ade80' }}>{counts.ativas}</b> <span style={{ color: 'var(--muted)' }}>ativas</span></span>
              <span><b style={{ fontSize: '1.15rem', color: 'var(--muted)' }}>{counts.aCriar}</b> <span style={{ color: 'var(--muted)' }}>a criar</span></span>
            </div>
          )}
        </motion.div>

        {loading && <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>carregando…</div>}
        {err && <div style={{ color: '#f87171', fontSize: '0.85rem' }}>erro ao carregar (token inválido ou API offline)</div>}

        {byArea.map((g, gi) => (
          <motion.section key={g.area} {...fade(0.05 + gi * 0.05)} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.85rem' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: AREA_COLOR[g.area] || 'var(--muted)' }} />
              <h2 style={{ fontSize: '0.72rem', color: 'var(--muted)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{g.area}</h2>
              <span style={{ fontSize: '0.7rem', color: 'var(--muted)', opacity: 0.6 }}>{g.items.length}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {g.items.map(f => (
                <div key={f.slug} style={{
                  padding: '0.85rem 1.1rem', background: 'var(--bg2)', border: '1px solid var(--border)',
                  borderRadius: 9, borderLeft: `3px solid ${AREA_COLOR[f.area] || 'var(--border)'}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{f.titulo || f.slug}</span>
                    <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexShrink: 0 }}>
                      {f.exec && <span style={{ fontSize: '0.6rem', fontFamily: 'monospace', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.05rem 0.4rem' }}>{f.exec}</span>}
                      {f.gate_decision && <span style={{ fontSize: '0.6rem', fontFamily: 'monospace', color: f.gate_decision === 'PASS' ? '#4ade80' : f.gate_decision === 'FAIL' ? '#f87171' : '#fbbf24', border: '1px solid var(--border)', borderRadius: 4, padding: '0.05rem 0.4rem' }}>{f.gate_decision}</span>}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'monospace' }}>{f.slug}</span>
                    {f.status && <span style={{ fontSize: '0.76rem', color: 'var(--muted)', textAlign: 'right' }}>{f.status}</span>}
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        ))}
      </main>
    </>
  )
}
